import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { HttpError } from '../utils/httpError';
import { useApiFetch } from './useApiFetch';

export interface ApiQueryResult<T> {
  /**
   * The current response, or the last response this hook returned for the same
   * caller and endpoint while the current key has none (a new filter or page that is
   * still loading, or that failed).
   */
  data: T | undefined;
  error: Error | null;
  /** Any request in flight, including a quiet refresh of what is shown. */
  isFetching: boolean;
  /** A request for data the caller does not have yet: no rows, or only the
   *  previous filter's. Refreshing rows already shown is not loading. */
  isLoading: boolean;
  isPending: boolean;
  /** `data` belongs to an earlier key: the previous filter or page. */
  isPlaceholderData: boolean;
  refetch: UseQueryResult<T, Error>['refetch'];
}

/** Cache list responses across page visits; refresh without clearing the rows. */
export function useApiQuery<T>(
  url: string,
  enabled = true,
  options: {
    staleTime?: number;
    gcTime?: number;
    refetchOnWindowFocus?: boolean;
  } = {}
): ApiQueryResult<T> {
  const { user } = useAuthContext();
  const { apiFetch } = useApiFetch();
  const caller = user?.sub ?? null;
  const endpoint = url.split('?')[0];
  const query = useQuery<T>({
    queryKey: ['api', caller, endpoint, url],
    enabled: enabled && caller !== null,
    staleTime: options.staleTime ?? 15_000,
    gcTime: options.gcTime,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    retry: false,
    // Previous filters may stay visible while fetching. Another user's data
    // must never become placeholder data if the authenticated identity changes.
    placeholderData: (previous, query) =>
      query?.queryKey[1] === caller && query.queryKey[2] === endpoint
        ? previous
        : undefined,
    queryFn: async ({ signal }) => {
      let response: Response | null;
      try {
        response = await apiFetch(
          url,
          { signal },
          { returnNonOkResponse: true, throwErrors: true }
        );
      } catch (cause) {
        const reason = cause instanceof Error ? cause.message : String(cause);
        throw new Error(`Could not load results (${reason})`, { cause });
      }
      if (!response) throw new Error('Could not load results');
      if (!response.ok)
        throw new HttpError(
          `Could not load results (HTTP ${response.status})`,
          response.status
        );
      return (await response.json()) as T;
    },
  });
  // placeholderData covers pending requests but disappears when a new key
  // fails. Keep only this consumer's last result through that error: another
  // list or search using the same endpoint may have unrelated cached rows.
  const key = `${caller}\0${endpoint}`;
  const [lastGood, setLastGood] = useState<{
    key: string;
    data: T | undefined;
  }>({
    key,
    data: query.data,
  });
  if (
    lastGood.key !== key ||
    (query.data !== undefined && query.data !== lastGood.data)
  ) {
    setLastGood({ key, data: query.data });
  }
  const fallback =
    query.data === undefined && caller !== null && lastGood.key === key
      ? lastGood.data
      : undefined;
  const isPlaceholderData =
    query.isPlaceholderData ||
    (query.data === undefined && fallback !== undefined);
  return {
    data: query.data ?? fallback,
    error: query.error,
    isFetching: query.isFetching,
    isLoading:
      query.isFetching && (query.data === undefined || isPlaceholderData),
    isPending: query.isPending,
    isPlaceholderData,
    refetch: query.refetch,
  };
}
